import { v4 } from 'uuid';
import type { App } from '../app/App';
import type { OfferLocation, OfferTrigger } from '../app/offers/offersTypes';
import { SpecialOfferPopup } from '../mainMenu/offers/SpecialOfferPopup';
import type { IAPPlacement, IAPProduct, IAPProductWithPopupData } from '../app/iap/types';
import { OfferInteractionLocation } from '../app/tracking/types';
import { preloadLayoutsAssets } from '../views/preloadLayoutsAssets';

const openedOffers = new Set<string>();

export async function openOfferPopup(app: App, offerToShow: IAPProduct, placement: IAPPlacement): Promise<void> {
  if (offerToShow.endDate && offerToShow.endDate < Date.now()) return;
  if (!offerToShow.data.popupLayout) return;

  const {
    controller: { popups },
    tracking,
    iap,
  } = app;

  if (popups.isAnyPopupOpened()) return;

  // Preload assets required by the popup layout (supports path or raw)
  const { popupLayout } = (offerToShow as IAPProductWithPopupData).data;
  const layoutInput = popupLayout?.raw ? popupLayout.raw : popupLayout?.path ?? null;
  if (layoutInput) {
    const ok = await preloadLayoutsAssets([layoutInput], true);
    if (!ok) return;
  } else {
    return;
  }

  app.platform.logEvent(`Seen Fullscreen Offer`);

  const impressionId = v4();

  tracking.trackOfferShown({
    product: offerToShow,
    location: OfferInteractionLocation.OfferPopup,
    impressionId,
  });

  const popup = new SpecialOfferPopup(offerToShow as IAPProductWithPopupData);

  void popups.addPopup({
    settings: { overlay: false, hideRays: true },
    popupInstance: popup,
  });

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const { resolve, promise } = Promise.withResolvers<void>();

  popup.onCloseClicked.connect(async () => {
    await popups.closeActivePopup();
    resolve();
  });

  popup.onBuyClicked.connect(async () => {
    if (!iap) return;
    const success = await iap.makePurchase({
      product: offerToShow,
      placement,
      showResults: true,
      offerImpressionId: impressionId,
    });
    if (!success) return;
    await popups.closeActivePopup();
    resolve();
  });

  await promise;
}

export async function findOfferAndOpenPopup(
  app: App,
  location: OfferLocation,
  trigger: OfferTrigger | null,
  placement: IAPPlacement,
): Promise<void> {
  const { iap } = app;

  if (!iap) return;

  const catalog = await iap.getCatalogAsync(location, trigger);
  if (!catalog) return;

  const offerToShow = catalog.filter((offer) => !openedOffers.has(offer.id) && offer.data.popupLayout)[0] ?? null;

  if (!offerToShow) return;

  openedOffers.add(offerToShow.id);

  await openOfferPopup(app, offerToShow, placement);
}
